// corporate-reporting-store.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, forkJoin } from 'rxjs';
import {
   catchError,
   distinctUntilChanged,
   map,
   shareReplay,
   switchMap,
   tap
} from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ReportService } from '../report.service';
import { ReportRequest } from '../request/report-request';
import { StaffOrganisationReportRow } from '../../domain/corporate/staff-organisation-report';
import { TaskOrganisationGroupReportRow } from '../../domain/corporate/task-group-report';
import { PerformanceHotelRow } from '../../domain/corporate/performance-hotel-row';
import { PropertyStoreService } from './property-store.service';
import { OrganisationContextStoreService } from './organisation-context-store.service';
import { OrganisationService } from '../organisation.service';
import { DEFAULT_HOTEL_RISK_AMOUNT_THRESHOLDS } from '../../domain/corporate/hotel-risk-amount-thresholds';
import { HighRiskHotelRow } from '../../domain/corporate/high-risk-hotel-row';
import {
   CorporateFilters,
   CorporateDatePreset
} from '../../domain/corporate/corporate-filters';
import { CorporatePropertyAiSummary } from '../../domain/Ai/corporate-property-ai-summary';
import { Property } from '../../domain/property';
import { PortfolioSnapshot } from '../../domain/corporate/portfolio-snapshot';
import { CorporateApiQuery } from '../../domain/corporate/corporate-api-query';
import { PortfolioRiskSeries } from '../../domain/corporate/portfolio-risk-series';

import { TaskOrganisationReportRow } from '../../domain/corporate/task-organisation-report-row';
import { HighRiskReportRow } from '../../domain/corporate/high-risk-report-row';
import { DEFAULT_REPORT_DAYS_RISK_THRESHOLDS } from '../../domain/corporate/report-days-risk-thresholds';
import { RiskLevel } from '../../domain/corporate/risk-level';
import { ReportPropertyBreakdownRow } from '../../domain/corporate/report-property-breakdown-row';
import { StaffPerformanceRowVm } from '../../domain/corporate/staff-performance-row-vm';

@Injectable({ providedIn: 'root' })
export class CorporateReportingStoreService {
   private filtersSubject = new BehaviorSubject<CorporateFilters>({
      propertyId: null,
      preset: 'last30'
   });

   readonly filters$ = this.filtersSubject.asObservable();

   private loadingSubject = new BehaviorSubject<boolean>(false);
   readonly loading$ = this.loadingSubject.asObservable();

   private errorStatusSubject = new BehaviorSubject<number | null>(null);
   readonly errorStatus$ = this.errorStatusSubject.asObservable();

   private performanceLoadingSubject = new BehaviorSubject<boolean>(false);
   readonly performanceLoading$ = this.performanceLoadingSubject.asObservable();

   private performanceErrorStatusSubject = new BehaviorSubject<number | null>(null);
   readonly performanceErrorStatus$ =
      this.performanceErrorStatusSubject.asObservable();

   readonly performanceRows$!: Observable<PerformanceHotelRow[]>;
   readonly topPerformingHotels$!: Observable<PerformanceHotelRow[]>;
   readonly underPerformingHotels$!: Observable<PerformanceHotelRow[]>;
   readonly staffPerformanceByPropertyId$!: Observable<
      Map<number, StaffPerformanceRowVm[]>
   >;

   readonly riskTitle$!: Observable<string>;

   private dateRange$!: Observable<{ startDate: string; endDate: string }>;
   private selectedPropertyId$!: Observable<number | null>;

   readonly apiQuery$!: Observable<CorporateApiQuery>;
   readonly data$!: Observable<CorporatePropertyAiSummary[]>;
   readonly properties$!: Observable<Property[]>;
   readonly filteredData$!: Observable<CorporatePropertyAiSummary[]>;
   readonly snapshot$!: Observable<PortfolioSnapshot>;
   readonly riskSeries$!: Observable<PortfolioRiskSeries>;

   readonly highRiskHotelsCount$!: Observable<number>;
   readonly highRiskReportsCount$!: Observable<number>;
   readonly highRiskHotels$!: Observable<HighRiskHotelRow[]>;

   private reportsLoadingSubject = new BehaviorSubject<boolean>(false);
   readonly reportsLoading$ = this.reportsLoadingSubject.asObservable();

   private reportsErrorStatusSubject = new BehaviorSubject<number | null>(null);
   readonly reportsErrorStatus$ = this.reportsErrorStatusSubject.asObservable();

   readonly highRiskReportRows$!: Observable<HighRiskReportRow[]>;
   readonly daysInRange$!: Observable<number>;
   readonly reportBreakdownByName$!: Observable<
      Record<string, ReportPropertyBreakdownRow[]>
   >;

   constructor(
      private orgContext: OrganisationContextStoreService,
      private organisationService: OrganisationService,
      private reportService: ReportService,
      private propertyStore: PropertyStoreService
   ) {
      // 1) Date range derived from filters (preset/custom).
      this.dateRange$ = this.filters$.pipe(
         tap((f) => console.log('[Corporate Filters]', f)),
         map((filters) => {
            const today = DateTime.now();
            return this.resolveDateRange(
               today,
               filters.preset,
               filters.customStart,
               filters.customEnd
            );
         }),
         distinctUntilChanged(
            (a, b) => a.startDate === b.startDate && a.endDate === b.endDate
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.daysInRange$ = this.dateRange$.pipe(
         map((r) => this.buildIsoDateRange(r.startDate, r.endDate).length), // inclusive
         distinctUntilChanged(),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 2) API query = orgId + date range only (propertyId should NOT affect this).
      this.apiQuery$ = combineLatest([
         this.orgContext.organisationId$,
         this.dateRange$
      ]).pipe(
         map(([organisationId, range]) => ({
            organisationId,
            startDate: range.startDate,
            endDate: range.endDate
         })),
         tap((q) => console.log('[Corporate API Query]', q)),
         distinctUntilChanged(
            (a, b) =>
               a.organisationId === b.organisationId &&
               a.startDate === b.startDate &&
               a.endDate === b.endDate
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 3) Raw API data (all properties under org for the selected date range).
      this.data$ = this.apiQuery$.pipe(
         switchMap((query) => {
            if (!query.organisationId) {
               this.loadingSubject.next(false);
               this.errorStatusSubject.next(null);
               return of([]);
            }

            this.loadingSubject.next(true);
            this.errorStatusSubject.next(null);

            return this.organisationService
               .getOrganisationAiSummary(
                  query.organisationId,
                  query.startDate,
                  query.endDate
               )
               .pipe(
                  tap(() => this.loadingSubject.next(false)),
                  catchError((err) => {
                     this.loadingSubject.next(false);
                     this.errorStatusSubject.next(err?.status ?? 500);
                     return of([]);
                  })
               );
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 4) Property list for dropdown (from loaded data).
      this.properties$ = this.data$.pipe(
         map((rows) =>
            rows
               .map((r) => r.property)
               .filter((p): p is Property => !!p?.id)
               .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 5) Selected property only (client-side filtering).
      this.selectedPropertyId$ = this.filters$.pipe(
         map((f) => f.propertyId),
         distinctUntilChanged(),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 6) Data filtered by selected property (null = all).
      this.filteredData$ = combineLatest([this.data$, this.selectedPropertyId$]).pipe(
         map(([rows, propertyId]) => {
            if (!propertyId) return rows;
            return rows.filter((r) => r.property?.id === propertyId);
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 7) Portfolio Snapshot (totals based on filteredData$)
      this.snapshot$ = this.filteredData$.pipe(
         map((rows) => {
            const propertiesCount = rows.length;

            // Sum tasks launched across selected properties
            const totalTasksInstanceLaunched = rows.reduce(
               (sum, r) => sum + (r.totalTasksInstanceLaunched ?? 0),
               0
            );

            // Find latest AI run date across selected properties
            const lastAiRunDate =
               rows
                  .map((r) => r.lastAiRunDate)
                  .filter((d): d is string => !!d)
                  .sort()
                  .at(-1) ?? null;

            // Flatten all day summaries across all selected properties
            const allDays = rows.flatMap((r) => r.summaryResponses ?? []);

            const totalProcessed = allDays.reduce(
               (sum, d) => sum + (d.processed ?? 0),
               0
            );
            const totalCompleted = allDays.reduce(
               (sum, d) => sum + (d.completed ?? 0),
               0
            );
            const totalErrors = allDays.reduce((sum, d) => sum + (d.errors ?? 0), 0);

            // Flatten all model summaries, excluding errors (same logic as your property dashboard)
            const allModels = allDays
               .flatMap((d) => d.modelSummaries ?? [])
               .filter((m) => !m.hasError);

            // Financial risk = sum of amount where isFinancial=true
            const financialRiskAmount = allModels
               .filter((m) => m.isFinancial)
               .reduce((sum, m) => sum + (m.amount ?? 0), 0);

            // Non-financial items = sum of summaryData length where isFinancial=false
            const nonFinancialItemsCount = allModels
               .filter((m) => !m.isFinancial)
               .reduce((sum, m) => sum + this.getNonFinancialCount(m), 0);

            return {
               propertiesCount,
               totalTasksInstanceLaunched,
               lastAiRunDate,
               totalProcessed,
               totalCompleted,
               totalErrors,
               financialRiskAmount,
               nonFinancialItemsCount
            };
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 8) Risk chart series (by day) based on filteredData$ + current date range
      this.riskSeries$ = combineLatest([this.filteredData$, this.apiQuery$]).pipe(
         map(([rows, query]) => {
            // No org? return empty chart
            if (!query.organisationId) {
               return {
                  labels: [],
                  financialRiskByDay: [],
                  nonFinancialTasksByDay: []
               };
            }

            const isoDates = this.buildIsoDateRange(query.startDate, query.endDate);

            // Seed every day with 0 values (so missing days still show on chart)
            const totalsByDay = new Map<
               string,
               { financial: number; nonFinancialTasks: number }
            >();
            isoDates.forEach((d) =>
               totalsByDay.set(d, { financial: 0, nonFinancialTasks: 0 })
            );

            // Aggregate
            for (const row of rows) {
               for (const day of row.summaryResponses ?? []) {
                  const dayKey = day.date;
                  if (!dayKey) continue;

                  const bucket = totalsByDay.get(dayKey) ?? {
                     financial: 0,
                     nonFinancialTasks: 0
                  };

                  for (const model of day.modelSummaries ?? []) {
                     if (model.hasError) continue;

                     if (model.isFinancial) {
                        bucket.financial += model.amount ?? 0;
                     } else {
                        bucket.nonFinancialTasks += this.getNonFinancialCount(model);
                     }
                  }

                  totalsByDay.set(dayKey, bucket);
               }
            }

            return {
               labels: isoDates.map((d) => DateTime.fromISO(d).toFormat('dd LLL')),
               financialRiskByDay: isoDates.map(
                  (d) => totalsByDay.get(d)?.financial ?? 0
               ),
               nonFinancialTasksByDay: isoDates.map(
                  (d) => totalsByDay.get(d)?.nonFinancialTasks ?? 0
               )
            };
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 9) Performance report request (org + date range)
      const performanceRequest$: Observable<{
         organisationId: number | null;
         request: ReportRequest;
      }> = combineLatest([this.orgContext.organisationId$, this.dateRange$]).pipe(
         map(([organisationId, range]) => {
            const request = new ReportRequest();
            // workflowIds already defaults to []
            request.start = range.startDate;
            request.finish = range.endDate;

            return { organisationId, request };
         }),
         distinctUntilChanged(
            (a, b) =>
               a.organisationId === b.organisationId &&
               a.request.start === b.request.start &&
               a.request.finish === b.request.finish
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 10) Fetch both performance endpoints in parallel
      const performanceRaw$ = performanceRequest$.pipe(
         switchMap(({ organisationId, request }) => {
            if (!organisationId) {
               this.performanceLoadingSubject.next(false);
               this.performanceErrorStatusSubject.next(null);
               return of({
                  staff: [] as StaffOrganisationReportRow[],
                  groups: [] as TaskOrganisationGroupReportRow[]
               });
            }

            this.performanceLoadingSubject.next(true);
            this.performanceErrorStatusSubject.next(null);

            return forkJoin({
               staff: this.reportService.getOrganisationStaffReport(
                  organisationId,
                  request
               ),
               groups: this.reportService.getOrganisationTaskGroupReport(
                  organisationId,
                  request
               )
            }).pipe(
               tap(({ staff }) => this.logStaffReportRaw(staff)),
               tap(() => this.performanceLoadingSubject.next(false)),
               catchError((err) => {
                  this.performanceLoadingSubject.next(false);
                  this.performanceErrorStatusSubject.next(err?.status ?? 500);
                  return of({
                     staff: [] as StaffOrganisationReportRow[],
                     groups: [] as TaskOrganisationGroupReportRow[]
                  });
               })
            );
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // Staff drilldown data (propertyId -> staff rows)
      this.staffPerformanceByPropertyId$ = performanceRaw$.pipe(
         map(({ staff }) => this.buildStaffPerformanceByPropertyId(staff)),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 11) Build performance rows, then apply property filter
      const allPerformanceRows$ = combineLatest([
         performanceRaw$,
         this.propertyStore.properties // all properties user has access to (for name lookup)
      ]).pipe(
         map(([raw, props]) =>
            this.buildPerformanceRows(raw.staff, raw.groups, props)
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.performanceRows$ = combineLatest([
         allPerformanceRows$,
         this.selectedPropertyId$
      ]).pipe(
         map(([rows, propertyId]) => {
            if (!propertyId) return rows;
            return rows.filter((r) => r.propertyId === propertyId);
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // 12) Top + Under lists
      this.topPerformingHotels$ = this.performanceRows$.pipe(
         map((rows) =>
            [...rows]
               .sort((a, b) => {
                  // primary: completion rate desc, secondary: avgDays asc
                  if (b.auditCompletionRate !== a.auditCompletionRate)
                     return b.auditCompletionRate - a.auditCompletionRate;
                  return a.avgDaysToAudit - b.avgDaysToAudit;
               })
               .slice(0, 5)
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.underPerformingHotels$ = this.performanceRows$.pipe(
         map((rows) =>
            [...rows]
               .sort((a, b) => {
                  // primary: completion rate asc, secondary: avgDays desc
                  if (a.auditCompletionRate !== b.auditCompletionRate)
                     return a.auditCompletionRate - b.auditCompletionRate;
                  return b.avgDaysToAudit - a.avgDaysToAudit;
               })
               .slice(0, 5)
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.riskTitle$ = combineLatest([this.filters$, this.dateRange$]).pipe(
         map(([filters, range]) =>
            this.buildRiskTitle(filters.preset, range.startDate, range.endDate)
         ),
         distinctUntilChanged(),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // ---- High Risk Hotels ----
      this.highRiskHotels$ = combineLatest([this.filteredData$, this.apiQuery$]).pipe(
         map(([rows, query]) => {
            const isoDates = this.buildIsoDateRange(query.startDate, query.endDate);
            const dayCount = Math.max(isoDates.length, 1); // avoid divide by zero

            return (
               rows
                  .map((r) => {
                     const propertyId = r.property?.id ?? 0;
                     const hotelName = r.property?.name ?? `Property ${propertyId}`;

                     const reportsAtRisk = this.countReportsAtRisk(r);

                     const avgDailyFinancialImpact =
                        this.getAvgDailyFinancialAmount(r, isoDates) / dayCount;

                     const status = this.getHotelRiskLevelByAvgDaily(
                        avgDailyFinancialImpact
                     );
                     const statusRank =
                        status === 'High' ? 3 : status === 'Medium' ? 2 : 1;

                     return {
                        propertyId,
                        hotelName,
                        reportsAtRisk,
                        // NOTE: this now represents AVG per day (matches attachment logic)
                        financialImpact: avgDailyFinancialImpact,
                        status,
                        statusRank
                     } satisfies HighRiskHotelRow;
                  })
                  // Keep only hotels that have *any* risk signal
                  .filter(
                     (x) => (x.reportsAtRisk ?? 0) > 0 || (x.financialImpact ?? 0) > 0
                  )
            );
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.highRiskHotelsCount$ = this.highRiskHotels$.pipe(
         map((rows) => rows.filter((r) => r.status === 'High').length),
         distinctUntilChanged(),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // ---- High Risk Reports (Task report by organisation) ----
      const taskReportRequest$: Observable<{
         organisationId: number | null;
         request: ReportRequest;
      }> = combineLatest([this.orgContext.organisationId$, this.dateRange$]).pipe(
         map(([organisationId, range]) => {
            const request = new ReportRequest();
            request.start = range.startDate;
            request.finish = range.endDate;
            return { organisationId, request };
         }),
         distinctUntilChanged(
            (a, b) =>
               a.organisationId === b.organisationId &&
               a.request.start === b.request.start &&
               a.request.finish === b.request.finish
         ),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      const taskReportRaw$: Observable<TaskOrganisationReportRow[]> =
         taskReportRequest$.pipe(
            switchMap(({ organisationId, request }) => {
               if (!organisationId) {
                  this.reportsLoadingSubject.next(false);
                  this.reportsErrorStatusSubject.next(null);
                  return of([] as TaskOrganisationReportRow[]);
               }

               this.reportsLoadingSubject.next(true);
               this.reportsErrorStatusSubject.next(null);

               return this.reportService
                  .getOrganisationTaskReport(organisationId, request)
                  .pipe(
                     tap((rows) => {
                        console.log(rows);
                        console.groupCollapsed(
                           `[Task Report RAW] rows=${rows.length}`
                        );
                        console.table(
                           rows.slice(0, 50).map((r) => ({
                              orgId: r.orgId,
                              propertyId: r.propertyId,
                              name: r.name,
                              totalTasks: r.totalTasks,
                              completed: r.completed,
                              daysToComplete: r.daysToComplete,
                              isAiEnabled: r.isAiEnabled
                           }))
                        );
                        console.groupEnd();
                     }),
                     tap(() => this.reportsLoadingSubject.next(false)),
                     catchError((err) => {
                        this.reportsLoadingSubject.next(false);
                        this.reportsErrorStatusSubject.next(err?.status ?? 500);
                        return of([] as TaskOrganisationReportRow[]);
                     })
                  );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
         );

      // Apply property filter (client-side, consistent with your dashboard)
      const taskReportFiltered$ = combineLatest([
         taskReportRaw$,
         this.selectedPropertyId$
      ]).pipe(
         map(([rows, propertyId]) => {
            if (!propertyId) return rows;
            return rows.filter((r) => r.propertyId === propertyId);
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.reportBreakdownByName$ = combineLatest([
         taskReportFiltered$,
         this.propertyStore.properties
      ]).pipe(
         map(([rows, props]) => this.buildReportBreakdownByName(rows, props)),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      // Aggregate by report name
      this.highRiskReportRows$ = taskReportFiltered$.pipe(
         map((rows) => this.aggregateHighRiskReports(rows)),
         shareReplay({ bufferSize: 1, refCount: true })
      );

      this.highRiskReportsCount$ = this.highRiskReportRows$.pipe(
         map((rows) => rows.filter((r) => r.risk === 'High').length),
         shareReplay({ bufferSize: 1, refCount: true })
      );
   }

   setFilters(filters: CorporateFilters): void {
      this.filtersSubject.next(filters);
   }

   // helper methods
   private buildIsoDateRange(startIso: string, endIso: string): string[] {
      const start = DateTime.fromISO(startIso).startOf('day');
      const end = DateTime.fromISO(endIso).startOf('day');

      if (!start.isValid || !end.isValid || end < start) {
         return [];
      }

      const days: string[] = [];
      let cursor = start;

      while (cursor <= end) {
         days.push(cursor.toISODate()!);
         cursor = cursor.plus({ days: 1 });
      }

      return days;
   }

   private resolveDateRange(
      today: DateTime,
      preset: CorporateDatePreset,
      customStart?: Date | null,
      customEnd?: Date | null
   ): { startDate: string; endDate: string } {
      if (preset === 'yesterday') {
         const d = today.minus({ days: 1 }).toISODate()!;
         return { startDate: d, endDate: d };
      }

      if (preset === 'last7') {
         return {
            startDate: today.minus({ days: 6 }).toISODate()!,
            endDate: today.toISODate()!
         };
      }

      if (preset === 'last30') {
         return {
            startDate: today.minus({ days: 29 }).toISODate()!,
            endDate: today.toISODate()!
         };
      }

      const start = customStart ? DateTime.fromJSDate(customStart).toISODate() : null;
      const end = customEnd ? DateTime.fromJSDate(customEnd).toISODate() : null;

      if (!start || !end) {
         return {
            startDate: today.minus({ days: 29 }).toISODate()!,
            endDate: today.toISODate()!
         };
      }

      return { startDate: start, endDate: end };
   }

   private buildPerformanceRows(
      staffRows: StaffOrganisationReportRow[],
      groupRows: TaskOrganisationGroupReportRow[],
      props: Property[]
   ): PerformanceHotelRow[] {
      const propNameById = new Map<number, string>();
      for (const p of props) {
         if (p?.id) propNameById.set(p.id, p.name ?? `Property ${p.id}`);
      }

      const staffByProp = new Map<number, StaffOrganisationReportRow>();
      for (const s of staffRows) staffByProp.set(s.propertyId, s);

      const groupsByProp = new Map<number, TaskOrganisationGroupReportRow>();
      for (const g of groupRows) groupsByProp.set(g.propertyId, g);

      const allPropertyIds = new Set<number>([
         ...staffRows.map((s) => s.propertyId),
         ...groupRows.map((g) => g.propertyId)
      ]);

      return Array.from(allPropertyIds).map((propertyId) => {
         const staff = staffByProp.get(propertyId);
         const groups = groupsByProp.get(propertyId);

         const hotelName = propNameById.get(propertyId) ?? `Property ${propertyId}`;

         // Completion rate = completed / claimed
         const totalClaimed =
            staff?.staffSummary?.reduce((sum, u) => sum + (u.totalClaimed ?? 0), 0) ??
            0;

         const totalCompleted =
            staff?.staffSummary?.reduce(
               (sum, u) => sum + (u.totalCompleted ?? 0),
               0
            ) ?? 0;

         const auditCompletionRate =
            totalClaimed > 0 ? (totalCompleted / totalClaimed) * 100 : 0;

         // Avg days to audit: prefer group avgTimeToComplete; fallback staff avgTimeToComplete
         const avgDaysToAudit =
            groups?.avgTimeToComplete ?? staff?.avgTimeToComplete ?? 0;

         const { mostEfficientGroup, leastEfficientGroup } =
            this.pickGroupEfficiency(groups);

         return {
            propertyId,
            hotelName,
            auditCompletionRate,
            avgDaysToAudit,
            mostEfficientGroup,
            leastEfficientGroup,
            lastAuditDate: null
         };
      });
   }

   private pickGroupEfficiency(groups?: TaskOrganisationGroupReportRow): {
      mostEfficientGroup: string | null;
      leastEfficientGroup: string | null;
   } {
      const stages = groups?.stageReports?.flatMap((sr) => sr.stages ?? []) ?? [];

      if (!stages.length) {
         return { mostEfficientGroup: null, leastEfficientGroup: null };
      }

      let min = stages[0];
      let max = stages[0];

      for (const s of stages) {
         if ((s.avgTimeToComplete ?? 0) < (min.avgTimeToComplete ?? 0)) min = s;
         if ((s.avgTimeToComplete ?? 0) > (max.avgTimeToComplete ?? 0)) max = s;
      }

      const mostEfficientGroup =
         (min.displayName ?? min.groupName ?? '').trim() || null;
      const leastEfficientGroup =
         (max.displayName ?? max.groupName ?? '').trim() || null;

      return { mostEfficientGroup, leastEfficientGroup };
   }

   private countReportsAtRisk(row: CorporatePropertyAiSummary): number {
      const models = this.getAllModels(row);
      return models.filter((m) => this.getNonFinancialCount(m) > 0).length;
   }

   private getAllModels(row: CorporatePropertyAiSummary): Array<{
      hasError?: boolean;
      isFinancial?: boolean;
      amount?: number;
      summaryData?: any[];
   }> {
      const days: any[] = (row as any)?.summaryResponses ?? [];
      const models: any[] = days.flatMap((d) => d?.modelSummaries ?? []);

      return models
         .filter((m) => !m?.hasError)
         .map((m) => ({
            hasError: m?.hasError,
            isFinancial: m?.isFinancial,
            amount: m?.amount,
            summaryData: m?.summaryData
         }));
   }

   private buildRiskTitle(
      preset: CorporateDatePreset,
      startIso: string,
      endIso: string
   ): string {
      // If custom is selected and range is valid, show explicit dates
      if (preset === 'custom') {
         if (startIso === endIso) {
            return `Risk Over ${this.formatIsoForTitle(startIso)}`;
         }
         return `Risk Over ${this.formatIsoForTitle(startIso)} - ${this.formatIsoForTitle(endIso)}`;
      }

      if (preset === 'yesterday') return 'Risk Over Yesterday';
      if (preset === 'last7') return 'Risk Over Last 7 Days';
      if (preset === 'last30') return 'Risk Over Last 30 Days';

      // fallback
      return 'Risk Over Selected Range';
   }

   private formatIsoForTitle(iso: string): string {
      const dt = DateTime.fromISO(iso);
      return dt.isValid ? dt.toFormat('dd/MM/yyyy') : iso;
   }

   private aggregateHighRiskReports(
      rows: TaskOrganisationReportRow[]
   ): HighRiskReportRow[] {
      // key = report name (merges duplicates across properties AND within same property)
      const map = new Map<
         string,
         {
            name: string;
            totalTasks: number;
            completed: number;

            weightedDaysSum: number; // UPDATED: now weighted by COMPLETED count (not totalTasks)
            weightSum: number; // UPDATED: now sum of COMPLETED counts only

            propertyIds: Set<number>;
            isAiEnabled: boolean;
         }
      >();

      for (const r of rows) {
         const name = (r.name ?? '').trim() || 'Unknown Report';

         const entry = map.get(name) ?? {
            name,
            totalTasks: 0,
            completed: 0,

            weightedDaysSum: 0,
            weightSum: 0,

            propertyIds: new Set<number>(),
            isAiEnabled: false
         };

         const totalTasks = Math.max(r.totalTasks ?? 0, 0);
         const completed = Math.max(r.completed ?? 0, 0);

         // keep totals exactly as before
         entry.totalTasks += totalTasks;
         entry.completed += completed;
         entry.propertyIds.add(r.propertyId);
         entry.isAiEnabled = entry.isAiEnabled || !!r.isAiEnabled;

         // -----------------------------
         // Avg Days logic fix
         // -----------------------------
         // Only include rows where we have at least 1 completed task.
         // This prevents "daysToComplete: 0" placeholders (when completed=0)
         // from dragging the average down.
         if (completed > 0) {
            const daysRaw = r.daysToComplete;
            const days = Number.isFinite(daysRaw) ? (daysRaw as number) : 0; // NEW (guard)

            // Weight by COMPLETED (not totalTasks)
            entry.weightedDaysSum += days * completed; // UPDATED
            entry.weightSum += completed; // UPDATED
         }

         map.set(name, entry);
      }

      return Array.from(map.values()).map((e) => {
         // Average is based only on completed tasks
         const avgDays = e.weightSum > 0 ? e.weightedDaysSum / e.weightSum : 0; // UPDATED

         const risk = this.getReportRiskLevel(avgDays);
         const riskRank = risk === 'High' ? 3 : risk === 'Medium' ? 2 : 1;

         return {
            name: e.name,
            avgDaysToComplete: avgDays,
            totalTasks: e.totalTasks,
            completed: e.completed,
            propertyCount: e.propertyIds.size,
            isAiEnabled: e.isAiEnabled,
            risk,
            riskRank
         };
      });
   }

   private getNonFinancialCount(model: any): number {
      if (Array.isArray(model?.summaryData)) return model.summaryData.length;
      return model?.amount ?? 0;
   }

   private getReportRiskLevel(avgDays: number): RiskLevel {
      const t = DEFAULT_REPORT_DAYS_RISK_THRESHOLDS;

      if (avgDays >= t.highMinDays) return 'High';
      if (avgDays >= t.mediumMinDays) return 'Medium';
      return 'Low';
   }

   private getAvgDailyFinancialAmount(
      row: CorporatePropertyAiSummary,
      isoDates: string[]
   ): number {
      // seed all days so missing days count as 0
      const byDay = new Map<string, number>();
      for (const d of isoDates) byDay.set(d, 0);

      for (const day of (row as any)?.summaryResponses ?? []) {
         const dayKey = day?.date;
         if (!dayKey || !byDay.has(dayKey)) continue;

         let sum = 0;
         for (const m of day?.modelSummaries ?? []) {
            if (m?.hasError) continue;
            if (m?.isFinancial) sum += m?.amount ?? 0;
         }

         // if multiple day entries exist, accumulate
         byDay.set(dayKey, (byDay.get(dayKey) ?? 0) + sum);
      }

      // returns TOTAL across the period; caller divides by dayCount
      let total = 0;
      for (const v of byDay.values()) total += v;
      return total;
   }

   private getHotelRiskLevelByAvgDaily(avgDailyAmount: number): RiskLevel {
      const t = DEFAULT_HOTEL_RISK_AMOUNT_THRESHOLDS;

      if (avgDailyAmount >= t.highMin) return 'High';
      if (avgDailyAmount >= t.mediumMin) return 'Medium';
      return 'Low';
   }

   private buildReportBreakdownByName(
      rows: TaskOrganisationReportRow[],
      props: Property[]
   ): Record<string, ReportPropertyBreakdownRow[]> {
      const propNameById = new Map<number, string>();
      for (const p of props) {
         if (p?.id) propNameById.set(p.id, p.name ?? `Property ${p.id}`);
      }

      // name -> propertyId -> aggregate
      const map = new Map<
         string,
         Map<
            number,
            {
               totalTasks: number;
               completed: number;
               weightedDaysSum: number; // weighted by completed
               weightSum: number; // sum of completed
            }
         >
      >();

      for (const r of rows) {
         const name = (r.name ?? '').trim() || 'Unknown Report';
         const propertyId = r.propertyId;

         const byProp =
            map.get(name) ??
            new Map<
               number,
               {
                  totalTasks: number;
                  completed: number;
                  weightedDaysSum: number;
                  weightSum: number;
               }
            >();

         const entry = byProp.get(propertyId) ?? {
            totalTasks: 0,
            completed: 0,
            weightedDaysSum: 0,
            weightSum: 0
         };

         const totalTasks = Math.max(r.totalTasks ?? 0, 0);
         const completed = Math.max(r.completed ?? 0, 0);

         entry.totalTasks += totalTasks;
         entry.completed += completed;

         // Only include days when completed > 0 (prevents 0 placeholders dragging avg down)
         if (completed > 0) {
            const daysRaw = r.daysToComplete;
            const days = Number.isFinite(daysRaw) ? (daysRaw as number) : 0;

            entry.weightedDaysSum += days * completed;
            entry.weightSum += completed;
         }

         byProp.set(propertyId, entry);
         map.set(name, byProp);
      }

      const result: Record<string, ReportPropertyBreakdownRow[]> = {};

      for (const [name, byProp] of map.entries()) {
         const rowsOut: ReportPropertyBreakdownRow[] = [];

         for (const [propertyId, e] of byProp.entries()) {
            const propertyName =
               propNameById.get(propertyId) ?? `Property ${propertyId}`;

            const completionRate =
               e.totalTasks > 0 ? (e.completed / e.totalTasks) * 100 : 0;

            // If nothing completed, we cannot calculate avgDays/risk for this property.
            const avgDays = e.weightSum > 0 ? e.weightedDaysSum / e.weightSum : null;

            rowsOut.push({
               propertyId,
               propertyName,
               totalTasks: e.totalTasks,
               completed: e.completed,
               completionRate,
               avgDaysToComplete: avgDays,
               risk: avgDays === null ? null : this.getReportRiskLevel(avgDays)
            });
         }

         // UPDATED: rows with averages first, then "--" rows; keep alphabetical within groups
         rowsOut.sort((a, b) => {
            const aHasAvg =
               a.avgDaysToComplete !== null && Number.isFinite(a.avgDaysToComplete);
            const bHasAvg =
               b.avgDaysToComplete !== null && Number.isFinite(b.avgDaysToComplete);

            if (aHasAvg !== bHasAvg) return aHasAvg ? -1 : 1;

            return (a.propertyName ?? '').localeCompare(b.propertyName ?? '');
         });

         result[name] = rowsOut;
      }

      return result;
   }

   // helper to build staff drilldown data
   private buildStaffPerformanceByPropertyId(
      staffRows: StaffOrganisationReportRow[]
   ): Map<number, StaffPerformanceRowVm[]> {
      const mapOut = new Map<number, StaffPerformanceRowVm[]>();

      for (const propRow of staffRows ?? []) {
         const propertyId = propRow.propertyId;

         const staffVm: StaffPerformanceRowVm[] = (propRow.staffSummary ?? []).map(
            (s) => {
               const claimed = Math.max(s.totalClaimed ?? 0, 0);
               const completed = Math.max(s.totalCompleted ?? 0, 0);
               const completionRate = claimed > 0 ? (completed / claimed) * 100 : 0;

               // Keep avg meaningful: only show when something was completed.
               const avgRaw = s.avgTimeToComplete;
               const avgDaysToComplete =
                  completed > 0 && Number.isFinite(avgRaw)
                     ? (avgRaw as number)
                     : null;

               return {
                  userId: s.userId,
                  fullName: (s.fullName ?? '').trim() || 'Unknown',
                  email: (s.email ?? '').trim(),
                  claimed,
                  completed,
                  completionRate,
                  avgDaysToComplete
               };
            }
         );

         // Default sort: best completion rate first, then completed desc, then name
         staffVm.sort((a, b) => {
            if (b.completionRate !== a.completionRate)
               return b.completionRate - a.completionRate;
            if (b.completed !== a.completed) return b.completed - a.completed;
            return a.fullName.localeCompare(b.fullName);
         });

         mapOut.set(propertyId, staffVm);
      }

      return mapOut;
   }

   private logStaffReportRaw(rows: StaffOrganisationReportRow[]): void {
      const safeRows = rows ?? [];

      console.groupCollapsed(`[Staff Report RAW] properties=${safeRows.length}`);

      console.table(
         safeRows.slice(0, 50).map((r) => ({
            propertyId: r.propertyId,
            avgTimeToClaim: r.avgTimeToClaim,
            avgTimeToComplete: r.avgTimeToComplete,
            staffCount: r.staffSummary?.length ?? 0,
            totalClaimed: (r.staffSummary ?? []).reduce(
               (sum, u) => sum + (u.totalClaimed ?? 0),
               0
            ),
            totalCompleted: (r.staffSummary ?? []).reduce(
               (sum, u) => sum + (u.totalCompleted ?? 0),
               0
            )
         }))
      );

      console.groupEnd();

      const flatStaff = safeRows.flatMap((pr) =>
         (pr.staffSummary ?? []).map((s) => ({
            propertyId: pr.propertyId,
            userId: s.userId,
            fullName: s.fullName,
            email: s.email,
            totalClaimed: s.totalClaimed,
            totalCompleted: s.totalCompleted,
            avgTimeToClaim: s.avgTimeToClaim,
            avgTimeToComplete: s.avgTimeToComplete
         }))
      );

      console.groupCollapsed(`[Staff Report RAW] staffRows=${flatStaff.length}`);

      console.table(flatStaff.slice(0, 50));
      console.groupEnd();
   }
}
